import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { truncateDefault } from '../../helper/functions'
import { produce } from 'immer'
import { MultiSelect } from '@blueprintjs/select'
import { Icon, MenuItem, Tooltip } from '@blueprintjs/core'
import { globalOnDemandVars } from '../../helper/context'
import { getFlatDataFromTree } from 'react-sortable-tree'
import { getNodeKey } from '../list/window1/functions'
import { hasScrollbarAlways } from '../../helper/CSSclasses'
import { css as classname } from 'emotion'
import { getInitialRecordProperty } from '../../logic/SM2+'
import { OverlayProps } from '../../helper/configs'
import { useTranslation } from 'react-i18next'

const nodeDataToLeftIcon = (nodeData) => {
  const parentNode = nodeData.parentNode
  const isRootNode = nodeData.node.id === 'rootNode'

  return (
    isRootNode ? null : (<span css={css`color: var(--gray8);`} title={!parentNode ? truncateDefault(globalOnDemandVars.topRootName(), true) : parentNode.title}>{(!parentNode ? truncateDefault(globalOnDemandVars.topRootName(), true) : truncateDefault(parentNode.title, true)) + ' >'}</span>)
  )
}

const itemPredicate = (query, nodeData, _index, exactMatch) => {
  const normalizedName = nodeData.node.title.toLowerCase()
  const normalizedQuery = query.toLowerCase()

  if (exactMatch) {
    return normalizedName === normalizedQuery
  } else {
    return normalizedName.indexOf(normalizedQuery) >= 0
  }
}

function SelectPartOfTree ({ node, popoverHeight, initialIds, onChangeCallback, placeholder, lazyData }, ref) {
  const { t } = useTranslation()

  const getData = useCallback(
    () => {
      const globalTreeData = node ? (node.children || []) : globalOnDemandVars.globalTreeData()
      const flatData = (getFlatDataFromTree({
        treeData: globalTreeData,
        getNodeKey: getNodeKey,
        ignoreCollapsed: false
      }))
      const TopNodeData = node ? {
        node: {
          ...node,
          children: globalTreeData
        }
      } : {
        node: {
          id: 'rootNode',
          title: globalOnDemandVars.topRootName(),
          subtitle: '',
          children: globalTreeData,
          height: 27,
          expanded: true,
          editableValues: {
            ...getInitialRecordProperty(),
            orderAsChild: 0
          }
        }
      }
      flatData.unshift(TopNodeData)
      return flatData
    }
    ,
    [node]
  )
  const [nodeDataArray, setNodeDataArray] = useState(lazyData ? [] : getData())

  const [query, setQuery] = useState('')

  const initialValues = useMemo(() => (!initialIds ? [] : (nodeDataArray.filter((nodeData) => (initialIds.some((id) => (id === nodeData.node.id)))))), [initialIds, nodeDataArray])

  const [selectedNodeData, setSelectedNodeData] = useState(initialValues)
  const isNodeDataSelected = useCallback(
    (nodeData) => {
      return selectedNodeData.some((selected) => (selected.node.id === nodeData.node.id))
    },
    [selectedNodeData]
  )
  const itemRenderer = useCallback(
    (nodeData, { modifiers, handleClick }) => {
      return <MenuItem
        active={modifiers.active}
        key={nodeData.node.id}
        // label={nodeData.key}
        onClick={handleClick}
        title={nodeData.node.title}
        text={nodeData.node.title ? truncateDefault(nodeData.node.title) : <span css={css`opacity: 0.5;`}>Blank</span>}
        icon={nodeDataToLeftIcon(nodeData)}
        label={isNodeDataSelected(nodeData) ? <Icon icon='tick' /> : null}
        shouldDismissPopover={false}
      />
    },
    [isNodeDataSelected]
  )
  const onNodeDataSelect = useCallback(
    (nodeData) => {
      if (!isNodeDataSelected(nodeData)) {
        const newSelectedNodeData = produce(selectedNodeData, draft => {
          draft.push(nodeData)
        })
        setSelectedNodeData(newSelectedNodeData)
        onChangeCallback && onChangeCallback(newSelectedNodeData)
        setQuery('')
      }
    },
    [onChangeCallback, isNodeDataSelected, selectedNodeData]
  )

  const onRemove = useCallback(
    (value, index) => {
      const newSelectedNodeData = produce(selectedNodeData, draft => {
        draft.splice(index, 1)
      })
      setSelectedNodeData(newSelectedNodeData)
      onChangeCallback && onChangeCallback(newSelectedNodeData)
    }
    ,
    [onChangeCallback, selectedNodeData]
  )
  // -----------------------------------------------------------------------------------------------------------
  const onOpening = useCallback(
    () => {
      setNodeDataArray(getData())
    }
    ,
    [getData]
  )
  const popoverProps = useMemo(() => (
    {
      minimal: true,
      popoverClassName: classname`overflow: auto; height: ${popoverHeight || '300'}px; background-color: white;`,
      position: 'top',
      onOpening: onOpening,
      ...OverlayProps
    }

  ), [onOpening, popoverHeight])
  const tagInputProps = useMemo(() => (
    {
      tagProps: { minimal: true, large: true },
      onRemove: onRemove
    }

  ), [onRemove])
  const tagRenderer = useCallback(
    (nodeData) => (
      <span title={nodeData.node.title}>{nodeData.node.title ? truncateDefault(nodeData.node.title, true) : <span css={css`opacity: 0.5;`}>Blank</span>}</span>)
    ,
    []
  )
  const noResults = useMemo(() => (<MenuItem disabled text={t('No results')} />), [t])

  const multiSelectRef = useRef(null)

  useImperativeHandle(
    ref,
    () => ({
      getSelectedNodeData: () => {
        return selectedNodeData
      },
      clearSelectedNodeData: () => {
        setSelectedNodeData([])
        onChangeCallback && onChangeCallback([])
      },
      focus: () => {
        multiSelectRef.current.input.focus()
      },
      blur: () => {
        multiSelectRef.current.input.blur()
      }
    }),
    [onChangeCallback, selectedNodeData]
  )

  return (
    <MultiSelect css={css`
& .bp3-input {
  border-radius: 1em;
  font-size: 12px !important;
}
`} ref={multiSelectRef} openOnKeyDown fill query={query} onQueryChange={setQuery} items={nodeDataArray} itemRenderer={itemRenderer} noResults={noResults}
      onItemSelect={onNodeDataSelect} placeholder={placeholder || t('Search and select nodes including their descendants')} selectedItems={selectedNodeData}
      tagRenderer={tagRenderer}
      itemPredicate={itemPredicate}
      popoverProps={popoverProps}
      tagInputProps={tagInputProps}
    />
  )
}

// eslint-disable-next-line no-func-assign
SelectPartOfTree = forwardRef(SelectPartOfTree)

export default SelectPartOfTree
